import { EmptyState, Skeleton, useErrorToast } from '@h2hspark/ui';
import { UserPlus } from 'lucide-react';
import * as React from 'react';

import { useMemberSession } from '@/auth/auth-provider';
import { PageBackLink } from '@/member/page-back-link';


import { useAnswerConnectionRequest, useMyConnectionRequests } from './api';
import { ConnectionRequestCard } from './connection-request-card';
import {
  CONNECTIONS_PATH,
  NO_REQUESTS_DESCRIPTION,
  NO_REQUESTS_TITLE,
  REQUESTS_EYEBROW,
  REQUESTS_TITLE,
  REQUEST_ACTION_FAILED,
  mostRecentRequestFirst,
} from './constants';

/**
 * The *Connection requests* screen (`MEM-032` AC-6).
 *
 * Reached from the connections list, which is one of the two entry points AC-6 names. The other
 * is the in-app notification, which is `MEM-NOT`'s and is not built — the route exists and is
 * deep-linkable, so that story adds a link rather than a screen.
 *
 * ## This screen has no prototype, by the specification's own account
 *
 * *"Neither prototype implements the inbound side of a connection request — there is no request
 * list, no accept and no decline. The requester's state changes to Request sent and nothing
 * further happens. MEM-CON-005 to MEM-CON-007 must be designed."* So it is built from the member
 * app's existing shapes: the connections list's header and column, and a card that borrows the
 * connection row's tile and the detail's event badge.
 *
 * ## Not an inbox, down to the icon — and since 2026-09-11 there is a real one to not be
 *
 * The word is worth being careful with. This is a **queue of consent decisions**, not a message
 * inbox: every row resolves exactly once and then leaves, there is no unread state, nothing is
 * marked read, no reply is possible, and a request that has been answered is served to nobody —
 * including the member who answered it.
 *
 * This paragraph used to end *"`MEM-CON-014`'s messages inbox is Phase 2 and this is not a first
 * version of it"*, and its glyph argument cited ADR 032. Both have gone stale in the same
 * direction: ADR 052 supersedes ADR 032 and `MEM-055` built `MEM-CON-014`'s Messages page, at
 * `/app/messages`, with unread state and a New pill. The distinction survives the change and
 * matters more for it — there are now two screens a member could confuse, so this one must not
 * wear the other's clothes. The empty state's glyph was an `Inbox` for one commit and a guard
 * caught it; it is a `UserPlus`, the same glyph the Connect action carries, because what lands
 * here is a person asking rather than a message arriving.
 *
 * ## Nothing here tells a requester anything
 *
 * Both answers produce the same 204 and neither sends a message; `MEM-032` AC-7's silence is
 * enforced in the service, which holds nothing that could send one. From this screen's side, the
 * only visible difference between Accept and Decline is whether the person then appears on the
 * connections list.
 */
export function ConnectionRequestsPage(): React.ReactElement {
  const { session } = useMemberSession();
  const query = useMyConnectionRequests({ enabled: session !== null });
  const answer = useAnswerConnectionRequest();
  const showError = useErrorToast();

  React.useEffect(() => {
    if (answer.error !== null) showError(answer.error, REQUEST_ACTION_FAILED);
  }, [answer.error, showError]);

  const requests = mostRecentRequestFirst(query.data?.requests ?? []);

  return (
    <div className="space-y-6">
      <PageBackLink to={CONNECTIONS_PATH}>
        Connections
      </PageBackLink>

      <header>
        <p className="h2h-eyebrow">{REQUESTS_EYEBROW}</p>
        <h1 className="mt-1 text-2xl">{REQUESTS_TITLE}</h1>
      </header>

      {query.isPending ? (
        <div className="space-y-3">
          <Skeleton className="h-[180px] w-full rounded-lg" />
          <Skeleton className="h-[180px] w-full rounded-lg" />
        </div>
      ) : requests.length === 0 ? (
        <EmptyState
          icon={<UserPlus className="h-6 w-6" aria-hidden />}
          title={NO_REQUESTS_TITLE}
          description={NO_REQUESTS_DESCRIPTION}
        />
      ) : (
        <ul className="space-y-3">
          {requests.map((request) => (
            <li key={request.requesterRef}>
              <ConnectionRequestCard request={request} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
